/**
 * Active devices — see where you are signed in, and sign a device out.
 *
 * Until now the product had no device control at all. The only thing that came close
 * was an accident: logout revoked every refresh token the user held, so people got a
 * global sign-out whether they wanted one or not. Once a logout ends a single session,
 * the global case needs a real surface — and a lost phone needs one badly.
 *
 * One refresh-token family is one device: created at sign-in, extended by each token
 * refresh, so it survives rotation and is the stable identity of "this device".
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Monitor, Loader2, LogOut, ShieldAlert, AlertCircle } from 'lucide-react';
import { authFetch } from '@/lib/authFetch';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { useAuth } from '@/contexts/AuthContext';

interface ActiveSession {
  session_id: string;
  device: string;
  ip_address: string | null;
  first_seen: string | null;
  last_seen: string | null;
  expires_at: string | null;
  current: boolean;
}

export function ActiveSessions() {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const { logout } = useAuth();

  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmingAll, setConfirmingAll] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      // The refresh token identifies which row is THIS device. It goes in the body,
      // never the query string, so it cannot land in an access log or history entry.
      const res = await authFetch('/api/auth/sessions', {
        method: 'POST',
        jsonBody: { refresh_token: localStorage.getItem('refresh_token') || undefined },
      });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      setSessions(data.sessions ?? []);
    } catch {
      setError(t('settings.sessions_load_failed', 'Could not load your devices. Try again in a moment.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const signOutDevice = async (session: ActiveSession) => {
    setBusyId(session.session_id);
    setError(null);
    try {
      const res = await authFetch(`/api/auth/sessions/${session.session_id}/revoke`, { method: 'POST' });
      if (!res.ok) throw new Error(String(res.status));
      // Signing out the device you are ON ends this session too — reflect that rather
      // than leaving the user on a page whose session is gone.
      if (session.current) {
        logout();
        return;
      }
      await load();
    } catch {
      setError(t('settings.sessions_revoke_failed', 'Could not sign that device out. Try again in a moment.'));
    } finally {
      setBusyId(null);
    }
  };

  const signOutEverywhere = async () => {
    setBusyId('all');
    setError(null);
    try {
      const res = await authFetch('/api/auth/sessions/revoke-all', { method: 'POST' });
      if (!res.ok) throw new Error(String(res.status));
      // This signs out the current device too, by design — so end the session locally
      // instead of leaving a page up that every request will now 401 on.
      logout();
    } catch {
      setError(t('settings.sessions_revoke_all_failed', 'Could not sign out everywhere. Try again in a moment.'));
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-gray-500 dark:text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
        <span className="text-sm">{t('settings.sessions_loading', 'Loading your devices…')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          {t('settings.active_devices', 'Active devices')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {t(
            'settings.active_devices_help',
            'Where you are currently signed in. Sign out any device you do not recognise.',
          )}
        </p>
      </div>

      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-300"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {sessions.length === 0 && !error && (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
          {t('settings.sessions_none', 'No other active devices.')}
        </p>
      )}

      <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-md border border-gray-200 dark:border-gray-700">
        {sessions.map((s) => (
          <li key={s.session_id} className="flex items-center gap-3 p-3">
            <Monitor className="w-5 h-5 text-gray-400 dark:text-gray-500 shrink-0" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{s.device}</span>
                {s.current && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300">
                    {t('settings.this_device', 'This device')}
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                {s.ip_address ? `${s.ip_address} · ` : ''}
                {s.last_seen
                  ? t('settings.last_active', 'Last active {{when}}', { when: fmt.relative(s.last_seen) })
                  : ''}
              </div>
            </div>
            <button
              type="button"
              onClick={() => signOutDevice(s)}
              disabled={busyId !== null}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busyId === s.session_id ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <LogOut className="w-3.5 h-3.5" aria-hidden="true" />
              )}
              {t('settings.sign_out_device', 'Sign out')}
            </button>
          </li>
        ))}
      </ul>

      <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
        {!confirmingAll ? (
          <button
            type="button"
            onClick={() => setConfirmingAll(true)}
            disabled={busyId !== null}
            className="inline-flex items-center gap-2 rounded-md border border-red-300 dark:border-red-800 px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-50"
          >
            <ShieldAlert className="w-4 h-4" aria-hidden="true" />
            {t('settings.sign_out_all_devices', 'Sign out of all devices')}
          </button>
        ) : (
          <div className="rounded-md border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-3 space-y-3">
            <p className="text-sm text-red-800 dark:text-red-200">
              {t(
                'settings.sign_out_all_confirm',
                'This signs you out everywhere immediately, including here, and you will need to sign in again. Use this if a device was lost or stolen.',
              )}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={signOutEverywhere}
                disabled={busyId !== null}
                className="inline-flex items-center gap-2 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {busyId === 'all' && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                {t('settings.sign_out_all_confirm_action', 'Sign out everywhere')}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingAll(false)}
                disabled={busyId !== null}
                className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        )}
        {/* Say what each control actually guarantees. Signing out one device stops it
            renewing immediately, but its current access token lives until it expires;
            the global sign-out is enforced on the next request everywhere. Implying
            otherwise would be the wrong thing to be vague about. */}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          {t(
            'settings.sign_out_scope_note',
            'Signing out one device stops it from staying signed in; it can take up to 24 hours to log out fully. Signing out of all devices takes effect immediately.',
          )}
        </p>
      </div>
    </div>
  );
}

export default ActiveSessions;
